import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Container, Row, Col, Badge, Button, Alert, Collapse, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Loader from '../../components/widgets/Loader';
import DynamicTable from '../../components/widgets/dynamicTable/DynamicTable';
import SelectMultipleItemsComponent from '../../components/widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent';
import axios from '../../services/axios';
import useBackButton from '../../hooks/useBackButton';
import useSearchWithParams from '../../hooks/useSearchWithParams';
import useCustomGetStoreState from '../../hooks/useCustomGetStoreState';
import HelperMetiers360 from '../../services/HelpersMetiers360';
import AuthorizationChecker from '../../services/AuthorizationChecker';
import OperationInscriptionModal from './OperationInscriptionModal';
import OperationInscriptionsBulkActionModal from './OperationInscriptionsBulkActionModal';

const OPERATION_STATUS_VARIANT = {
    draft: 'secondary',
    active: 'success',
    closed: 'danger',
};

const APPROVED_INSCRIPTION_STATUS = 'approved';

const PER_PAGE = 50;

const buildReference = (slug, id) =>
    `M360-${slug.toUpperCase()}-${String(id).padStart(4, '0')}`;

const OperationDetailPage = () => {
    const { uniqueId: slug } = useParams();
    const { apiData: { ACTIONS, BACKOFFICE_DEFAULT_VALUES } } = useStoreState(state => state.actionSlugs);
    const clientActionSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('clients'));
    const oneClientSlug = clientActionSlugs?.readOne ?? null;
    const { allClientsCRM, isFetchingAllClientsCRM } = useCustomGetStoreState('clients');
    const { fetchAllPropsForAllClients } = useStoreActions(actions => actions.clients);
    const { allTagsByUuid, isFetchingAllTagOperations } = useStoreState(state => state.tagOperations);
    const { fetchTagOperations } = useStoreActions(actions => actions.tagOperations);
    const [searchParam, setSearchParam] = useSearchParams();
    const { backButtonComponent } = useBackButton();
    const { searchInput, search } = useSearchWithParams({ placeholder: 'Rechercher par établissement, ville, UAI…', delay: 300 });

    const { operationStatus = [], inscriptionStatus = [], inscriptionStatusInjectionFailed } = BACKOFFICE_DEFAULT_VALUES;
    const operationStatusLabel = Object.fromEntries(operationStatus.map(s => [s.value, s.label]));
    const inscriptionStatusLabel = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.label]));
    const inscriptionStatusVariant = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.variant ?? 'secondary']));
    const inscriptionStatusByValue = Object.fromEntries(inscriptionStatus.map(s => [s.value, s]));

    const exportInscriptionsUrl = ACTIONS?.operations?.getSubEntities?.inscriptions?.exportInscriptions?.url;
    const exportEquipeUrl = ACTIONS?.operations?.getSubEntities?.inscriptions?.exportEquipe?.url;

    const [downloadingInscriptions, setDownloadingInscriptions] = useState(false);
    const [downloadingEquipe, setDownloadingEquipe] = useState(false);

    const downloadCsv = useCallback((url, filename, setDownloading) => {
        setDownloading(true);
        axios.get(url.replace('/slug/', `/${slug}/`), { responseType: 'blob' })
            .then(blob => {
                const href = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = href;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .finally(() => setDownloading(false));
    }, [slug]);

    const csvFilename = (type) => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
        return `${slug}_${type}_${date}_${time}.csv`;
    };
    const downloadExportInscriptions = () => downloadCsv(exportInscriptionsUrl, csvFilename('inscriptions'), setDownloadingInscriptions);
    const downloadExportEquipe = () => downloadCsv(exportEquipeUrl, csvFilename('equipe'), setDownloadingEquipe);

    const [showStatusLegend, setShowStatusLegend] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [operation, setOperation] = useState(null);
    const [items, setItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [selectedStatuts, setSelectedStatuts] = useState([]);
    const [activeInscriptionId, setActiveInscriptionId] = useState(null);
    const [selectedInscriptionIds, setSelectedInscriptionIds] = useState([]);
    const [showBulkActionModal, setShowBulkActionModal] = useState(null);

    const fetchInscriptions = useCallback(() => {
        const baseUrl = ACTIONS?.operations?.getSubEntities?.inscriptions?.readAll?.url;
        if (!baseUrl) return;
        const url = baseUrl.replace('/slug/', `/${slug}/`);

        setItems([]);
        setOperation(null);
        setLoading(true);
        setError(null);

        axios.get(`${url}?per_page=${PER_PAGE}&page=1`)
            .then(firstData => {
                setOperation(firstData.operation);

                if (firstData.total <= PER_PAGE) {
                    setItems(firstData.items);
                    return;
                }

                const extraPages = Math.ceil(firstData.total / PER_PAGE) - 1;
                return Promise.all(
                    Array.from({ length: extraPages }, (_, i) =>
                        axios.get(`${url}?per_page=${PER_PAGE}&page=${i + 2}`).then(d => d.items)
                    )
                ).then(results => {
                    setItems([...firstData.items, ...results.flat()]);
                });
            })
            .catch(() => setError('Impossible de charger les inscriptions.'))
            .finally(() => setLoading(false));
    }, [slug, ACTIONS]);

    useEffect(() => {
        fetchInscriptions();
    }, [fetchInscriptions]);

    useEffect(() => {
        if (AuthorizationChecker.hasReadAllRights('clientsCRM')) {
            fetchAllPropsForAllClients({ hasAccessToAllPropsClientsCRM: AuthorizationChecker.hasGetPropsForAllRights('clientsCRM') });
        }
        fetchTagOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setSelectedInscriptionIds([]);
        setFilteredItems(
            items.filter(item => {
                const matchStatut = selectedStatuts.length === 0
                    || selectedStatuts.some(s => s.value === item.statut);
                const matchSearch = !search
                    || HelperMetiers360.isSearchInText(search, item.etablissementNom)
                    || HelperMetiers360.isSearchInText(search, item.etablissementVille)
                    || HelperMetiers360.isSearchInText(search, item.uai)
                    || HelperMetiers360.isSearchInText(search, buildReference(slug, item.id));
                return matchStatut && matchSearch;
            })
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- slug is derived from route params and never changes
    }, [items, search, selectedStatuts]);

    const setInscriptionParam = (id) => {
        setSearchParam(prev => {
            const p = Object.fromEntries(prev.entries());
            if (id != null) p.inscriptionId = String(id);
            else delete p.inscriptionId;
            return p;
        });
    };

    const openInscriptionModal = (id) => {
        setActiveInscriptionId(id);
        setInscriptionParam(id);
    };

    useEffect(() => {
        if (loading || filteredItems.length === 0 || activeInscriptionId) return;
        const idStr = searchParam.get('inscriptionId');
        if (!idStr) return;
        const id = parseInt(idStr, 10);
        if (filteredItems.some(item => item.id === id)) {
            openInscriptionModal(id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, filteredItems, searchParam]);

    const clientsByUai = useMemo(() =>
        new Map(allClientsCRM.filter(c => c.uai).map(c => [c.uai, c])),
    [allClientsCRM]);

    const availableTagOperations = useMemo(() =>
        (operation?.tagOperations ?? []).map(uuid => allTagsByUuid.get(uuid)).filter(Boolean),
    [operation, allTagsByUuid]);

    const selectedInscriptions = useMemo(() =>
        filteredItems.filter(item => selectedInscriptionIds.includes(item.id)),
    [filteredItems, selectedInscriptionIds]);

    const closableInscriptions = useMemo(() =>
        selectedInscriptions.filter(item => item.statut === APPROVED_INSCRIPTION_STATUS && item.tagOperation),
    [selectedInscriptions]);
    const excludedFromClosureCount = selectedInscriptions.length - closableInscriptions.length;

    const editableInscriptions = useMemo(() =>
        selectedInscriptions.filter(item => inscriptionStatusByValue[item.statut]?.updatable !== false),
    [selectedInscriptions, inscriptionStatusByValue]);
    const excludedFromEditCount = selectedInscriptions.length - editableInscriptions.length;

    const handleStatusChanged = (id, newStatut) => setItems(prev => prev.map(item =>
        item.id === id ? { ...item, statut: newStatut } : item
    ));

    const handleTagChanged = (id, newTag) => setItems(prev => prev.map(item =>
        item.id === id ? { ...item, tagOperation: newTag } : item
    ));

    const contentSort = [
        {
            value: 'reference',
            label: 'Référence',
            test: items.length > 0,
            method: (a, b) => a.id - b.id,
            display: (item) => <code>{buildReference(slug, item.id)}</code>,
        },
        {
            value: 'uai',
            label: 'UAI',
            test: HelperMetiers360.isArrayContainsValue(items, 'uai'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.uai, b.uai),
            display: (item) => item.uai || '—',
        },
        {
            value: 'etablissementNom',
            label: 'Établissement',
            test: HelperMetiers360.isArrayContainsValue(items, 'etablissementNom'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.etablissementNom, b.etablissementNom),
            display: (item) => item.etablissementNom,
        },
        {
            value: 'etablissementCp',
            label: 'CP',
            test: HelperMetiers360.isArrayContainsValue(items, 'etablissementCp'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.etablissementCp, b.etablissementCp),
            display: (item) => item.etablissementCp,
        },
        {
            value: 'etablissementVille',
            label: 'Ville',
            test: HelperMetiers360.isArrayContainsValue(items, 'etablissementVille'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.etablissementVille, b.etablissementVille),
            display: (item) => item.etablissementVille,
        },
        {
            value: 'etablissementType',
            label: 'Catégorie',
            test: HelperMetiers360.isArrayContainsValue(items, 'etablissementType'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.etablissementType, b.etablissementType),
            display: (item) => item.etablissementType,
        },
        {
            value: 'createdAt',
            label: 'Soumis le',
            test: HelperMetiers360.isArrayContainsValue(items, 'createdAt'),
            method: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            display: (item) => item.createdAt
                ? new Date(item.createdAt).toLocaleDateString('fr-FR')
                : '—',
        },
        {
            value: 'tagOperation',
            label: 'Tag',
            test: !isFetchingAllTagOperations && HelperMetiers360.isArrayContainsValue(items, 'tagOperation'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(
                allTagsByUuid.get(a.tagOperation)?.name,
                allTagsByUuid.get(b.tagOperation)?.name,
            ),
            display: (item) => {
                const tag = item.tagOperation ? allTagsByUuid.get(item.tagOperation) : null;
                return tag ? <Badge bg="secondary">{tag.name}</Badge> : '—';
            },
        },
        {
            value: 'participationAnterieure',
            label: 'Participation antérieure ?',
            test: AuthorizationChecker.hasReadAllRights('clientsCRM')
                && !isFetchingAllClientsCRM
                && HelperMetiers360.isArrayContainsValue(items, 'uai'),
            method: (a, b) => (clientsByUai.has(b.uai) ? 1 : 0) - (clientsByUai.has(a.uai) ? 1 : 0),
            display: (item) => {
                if (!item.uai) return '—';
                const client = clientsByUai.get(item.uai);
                if (!client) return '—';
                const tags = client.tagOperationsName ?? [];
                return (
                    <OverlayTrigger overlay={<Tooltip>{client.name}</Tooltip>}>
                        <span onClick={e => e.stopPropagation()}>
                            {oneClientSlug
                                ? <Link to={oneClientSlug.replace(':uniqueId', client.uniqueId)}>
                                    {tags.length > 0
                                        ? tags.map(tag => <Badge key={tag} bg="info" className="me-1">{tag}</Badge>)
                                        : <Badge bg="info">{client.name}</Badge>
                                    }
                                </Link>
                                : tags.length > 0
                                    ? tags.map(tag => <Badge key={tag} bg="info" className="me-1">{tag}</Badge>)
                                    : <Badge bg="info">{client.name}</Badge>
                            }
                        </span>
                    </OverlayTrigger>
                );
            },
        },
        {
            value: 'statut',
            label: 'Statut',
            test: HelperMetiers360.isArrayContainsValue(items, 'statut'),
            method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.statut, b.statut),
            display: (item) => (
                <Badge bg={inscriptionStatusVariant[item.statut] ?? 'secondary'}>
                    {inscriptionStatusLabel[item.statut] ?? item.statut}
                </Badge>
            ),
        },
    ];

    return (
        <Container fluid>
            <Row className="mb-2">
                <Col>
                    <h2 className="d-flex justify-content-between align-items-center">
                        <span>
                            {operation?.nom ?? '…'}
                            {operation && (
                                <>
                                    <Badge
                                        bg={OPERATION_STATUS_VARIANT[operation.statut] ?? 'secondary'}
                                        className="ms-2"
                                    >
                                        {operationStatusLabel[operation.statut] ?? operation.statut}
                                    </Badge>
                                    {availableTagOperations.map(tag => (
                                        <Badge key={tag.uniqueId} bg="secondary" className="ms-1">{tag.name}</Badge>
                                    ))}
                                </>
                            )}
                        </span>
                        {backButtonComponent}
                    </h2>
                </Col>
            </Row>
            <Row className="mb-3">

                {(exportInscriptionsUrl || exportEquipeUrl) && (
                    <Col className="d-flex gap-2">
                        {exportInscriptionsUrl && (
                            <Button variant="outline-secondary" size="sm" onClick={downloadExportInscriptions} disabled={downloadingInscriptions}>
                                {downloadingInscriptions
                                    ? <Spinner animation="border" size="sm" className="me-1" />
                                    : <i className="fas fa-download me-1" />
                                }
                                Exporter les inscriptions
                            </Button>
                        )}
                        {exportEquipeUrl && (
                            <Button variant="outline-secondary" size="sm" onClick={downloadExportEquipe} disabled={downloadingEquipe}>
                                {downloadingEquipe
                                    ? <Spinner animation="border" size="sm" className="me-1" />
                                    : <i className="fas fa-download me-1" />
                                }
                                Exporter les membres
                            </Button>
                        )}
                    </Col>
                )}
                <Col className="d-flex justify-content-end gap-2">
                    <Button variant={selectedInscriptionIds?.length ? 'success' : 'outline-secondary'} disabled={!selectedInscriptionIds?.length} onClick={() => setShowBulkActionModal('edit')}>{`Modifier inscriptions en masse${selectedInscriptionIds?.length ? ` (${selectedInscriptionIds.length})` : ''}`}</Button>
                    <Button variant={selectedInscriptionIds?.length ? 'success' : 'outline-secondary'} disabled={!selectedInscriptionIds?.length} onClick={() => setShowBulkActionModal('close')}>{`Clôturer inscription(s)${selectedInscriptionIds?.length ? ` (${selectedInscriptionIds.length})` : ''}`}</Button>
                </Col>
            </Row>

            {inscriptionStatus.some(s => s.description) && (
                <Row className="mb-2">
                    <Col>
                        <Button
                            variant="link"
                            size="sm"
                            className="p-0 text-muted"
                            onClick={() => setShowStatusLegend(v => !v)}
                        >
                            <i className={`fas fa-chevron-${showStatusLegend ? 'up' : 'down'} me-1`} />
                            {showStatusLegend ? 'Masquer' : 'Afficher'} la légende des statuts
                        </Button>
                        <Collapse in={showStatusLegend}>
                            <div>
                                <Alert variant="info" className="mt-2 mb-0">
                                    {inscriptionStatus.map(s => (
                                        <div key={s.value} className="d-flex align-items-start gap-2 mb-1">
                                            <Badge bg={inscriptionStatusVariant[s.value] ?? 'secondary'} className="mt-1 flex-shrink-0">
                                                {s.label}
                                            </Badge>
                                            <span>{s.description}</span>
                                        </div>
                                    ))}
                                </Alert>
                            </div>
                        </Collapse>
                    </Col>
                </Row>
            )}

            <Row className="mb-3 align-items-center item-page">
                <Col md={5}>
                    {searchInput}
                </Col>
                <Col md={4}>
                    <SelectMultipleItemsComponent
                        items={inscriptionStatus}
                        isSorted={false}
                        itemsSelected={selectedStatuts}
                        setItemsSelected={setSelectedStatuts}
                        name="statutsInscription"
                        itemKey="value"
                        itemValue="label"
                        renderItemLabel={(s) => (
                            <><Badge bg={inscriptionStatusVariant[s.value] ?? 'secondary'} className="me-1">{s.label}</Badge></>
                        )}
                    />
                </Col>
            </Row>

            {loading && <Loader />}
            {!loading && error && <Alert variant="danger">{error}</Alert>}
            {!loading && !error && filteredItems.length === 0 && (
                <Alert variant="info">
                    {items.length === 0
                        ? 'Aucune inscription pour cette opération.'
                        : 'Aucune inscription ne correspond aux filtres.'}
                </Alert>
            )}
            {!loading && !error && filteredItems.length > 0 && (
                <>
                    {(isFetchingAllClientsCRM || isFetchingAllTagOperations) && <Loader />}
                    <DynamicTable
                        contentTable={filteredItems}
                        contentSort={contentSort}
                        index="id"
                        valueInitSort="createdAt"
                        handleClick={openInscriptionModal}
                        tableName="operationInscriptions"
                        withCheckBoxes
                        lineIndexesChecked={selectedInscriptionIds}
                        setLineIndexesChecked={setSelectedInscriptionIds}
                    />

                </>
            )}

            {activeInscriptionId && (
                <OperationInscriptionModal
                    inscriptionId={activeInscriptionId}
                    filteredItems={filteredItems}
                    onClose={() => { setActiveInscriptionId(null); setInscriptionParam(null); }}
                    onNavigate={openInscriptionModal}
                    onStatusChanged={handleStatusChanged}
                    onDeleted={(id) => { setItems(prev => prev.filter(item => item.id !== id)); setActiveInscriptionId(null); setInscriptionParam(null); }}
                    onTagChanged={handleTagChanged}
                    availableTagOperations={availableTagOperations}
                    ACTIONS={ACTIONS}
                    slug={slug}
                    inscriptionStatus={inscriptionStatus}
                />
            )}

            {showBulkActionModal && (
                <OperationInscriptionsBulkActionModal
                    mode={showBulkActionModal}
                    slug={slug}
                    inscriptions={showBulkActionModal === 'close' ? closableInscriptions : editableInscriptions}
                    excludedCount={showBulkActionModal === 'close' ? excludedFromClosureCount : excludedFromEditCount}
                    inscriptionStatus={inscriptionStatus}
                    injectionFailedStatus={inscriptionStatusInjectionFailed}
                    availableTagOperations={availableTagOperations}
                    ACTIONS={ACTIONS}
                    isClientKnown={(inscription) => clientsByUai.has(inscription.uai)}
                    onClose={() => setShowBulkActionModal(null)}
                    onRemoveInscription={(id) => setSelectedInscriptionIds(prev => prev.filter(selectedId => selectedId !== id))}
                    onStatusChanged={handleStatusChanged}
                    onTagChanged={handleTagChanged}
                    onActionCompleted={() => setSelectedInscriptionIds([])}
                />
            )}
        </Container>
    );
};

export default OperationDetailPage;
